import { ConfirmRequestDialog } from './index';
import React, { useContext, useEffect, useState } from 'react';
import { CompanyContext } from 'index';
import useSelector from 'hooks/useSelector';
import { userSelector } from 'store/user/selectors';
import {
  DIALOG_TYPES,
  useGlobalDialogContext,
} from 'App/components/Layouts/Dashboard/dialog/GlobalDialog';
import requestService from 'store/request/service';
import { ETwilioCallbackSmsStatus } from 'store/request/interfaces';
import { twilioUndeliveredSmsError } from '../../../../integrations/twilio/error/constants';
import { buildLinkToCandidateAuth } from 'functions/linkUtils';
import { useWebSocketClientMessageSubscription } from 'contexts/webSocketClient/useWebSocketClientSubscription';
import { WebSocketClientMessageTypes } from 'contexts/webSocketClient/messageTypes';
import { useLogsManager } from 'integrations/logs';

export const ConfirmRequestGlobalDialog = ({ request, title }: any) => {
  const user = useSelector(userSelector);
  const company = useContext(CompanyContext);
  const { hideDialog, showDialog } = useGlobalDialogContext();
  const logs = useLogsManager();
  const [isLoading, setLoading] = useState(false);
  const [lastUsedText, setLastUsedText] = useState('');
  const [smsDelivered, setSmsDelivered] = useState(false);
  const smsMessageStatusSubscription = useWebSocketClientMessageSubscription(
    WebSocketClientMessageTypes.SMS_MESSAGE_STATUS_UPDATED,
    300 * 1000
  );

  const [driverSmsStatus, setDriverSmsStatus] = useState<any>();

  const [sendDriverRequestSms, { error: errorSendingDriverRequestSms }] =
    requestService.endpoints.sendDriverRequestSms.useMutation();

  const [sendDriverRequestSmsToNewNumber] =
    requestService.endpoints.sendDriverRequestSmsToNewNumber.useMutation();

  const handleDialogToggle = () => {
    hideDialog();
  };

  useEffect(() => {
    if (driverSmsStatus?.status === ETwilioCallbackSmsStatus.delivered) {
      setSmsDelivered(true);
      return;
    }
    if (
      driverSmsStatus?.status &&
      [
        ETwilioCallbackSmsStatus.undelivered,
        ETwilioCallbackSmsStatus.failed,
      ].includes(driverSmsStatus.status as ETwilioCallbackSmsStatus)
    ) {
      setLoading(false);
      showDialog(DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG, {
        request,
        lastUsedText,
        error: {
          ...twilioUndeliveredSmsError,
          code: driverSmsStatus?.code || 0,
        },
      });
    }
  }, [driverSmsStatus]);

  useEffect(() => {
    if (errorSendingDriverRequestSms) {
      smsMessageStatusSubscription.unsubscribe();
      setLoading(false);
      showDialog(DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG, {
        request,
        lastUsedText,
        // @ts-ignore
        error: errorSendingDriverRequestSms?.data,
      });
    }
  }, [errorSendingDriverRequestSms]);

  const handleSendMessage = (
    text: string,
    newNumber?: string,
    documentRequestId?: string
  ) => {
    setLastUsedText(text);
    let requestId = request?.id;
    if (!requestId) {
      if (documentRequestId) {
        requestId = documentRequestId;
      } else {
        return;
      }
    }
    if (!newNumber) {
      sendDriverRequestSms({
        documentRequestId: requestId,
        data: {
          text,
        },
      })
        .unwrap()
        .then((result: any) => {
          if (result?.success) {
            setSmsDelivered(true);
          }
        });
    } else {
      sendDriverRequestSmsToNewNumber({
        documentRequestId: requestId,
        data: {
          text,
          number: newNumber,
        },
      });
    }

    smsMessageStatusSubscription.subscribe(
      (message: any) => {
        setDriverSmsStatus(message);
      },
      () => {
        setLoading(false);
        showDialog(DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG, {
          request,
          lastUsedText,
          error: twilioUndeliveredSmsError,
        });
        logs.logError('Timeout for SMS_MESSAGE_STATUS_UPDATED WS message', {
          documentRequestId,
        });
      }
    );
    setLoading(true);
  };

  return (
    <ConfirmRequestDialog
      isLoading={isLoading}
      isDelivered={smsDelivered}
      open={true}
      setOpen={() => {
        handleDialogToggle();
      }}
      companyName={company.name}
      manager={user}
      link={buildLinkToCandidateAuth(request?.candidateCode)}
      request={request}
      title={title}
      onSendMessage={handleSendMessage}
    ></ConfirmRequestDialog>
  );
};
